import React, { useEffect, useMemo, useRef, useState, useTransition } from 'react';
import {
	Alert,
	Button,
	Card,
	CircularProgress,
	Collapse,
	FormControl,
	IconButton,
	InputAdornment,
	MenuItem,
	Skeleton,
	Stack,
	Tooltip,
	Typography,
} from '@mui/material';
import { callAPIAsync, getAdminUsers, getAllLEAs, getOperationMetrics, getUploadMetrics } from '../../../../utils/api';
import { sortRecord } from '../../../../utils/string';
import { LEADetails } from '../../../../types/admin';
import { useLogto } from '@logto/react';
import { AdminUserDetails } from '../../../../types/user';
import { SparkLineChart } from '@mui/x-charts/SparkLineChart';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/en-gb';
import utc from 'dayjs/plugin/utc';
import { AxisItemIdentifier } from '@mui/x-charts';
import LabelledSelect from '../../../LabelledSelect';
import { ClearIcon, DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { excludedForces, processChartStats, processStats, statsHeaders } from './adminStatsUtils';
import { SnackbarAlert } from '../../../SnackbarAlert';
import { Clear, Download } from '@mui/icons-material';
import { useReactToPrint } from 'react-to-print';

dayjs.extend(utc);

const AdminDashboard: React.FC = () => {
	const [leas, setLEAs] = useState<LEADetails>({});
	const [users, setUsers] = useState<AdminUserDetails[]>([]);
	const [files, setFiles] = useState<Record<string, string>[]>([]);
	const [operations, setOperations] = useState<Record<string, string>[]>([]);

	const viewerRef = useRef<HTMLDivElement>(null);

	const [leaFilter, setLEAFilter] = useState<string>('');

	const [filterFromInput, setFilterFromInput] = useState<Dayjs | null>(null);
	const [filterToInput, setFilterToInput] = useState<Dayjs | null>(null);

	const [filterFrom, setFilterFrom] = useState<Dayjs | null>(null);
	const [filterTo, setFilterTo] = useState<Dayjs | null>(null);

	const [loadError, setLoadError] = useState<string>('');

	const [chartIndex, setChartIndex] = useState<number | null>(null);
	const logto = useLogto();

	const [loadingMetrics, startTransition] = useTransition();

	useEffect(() => {
		startTransition(async () => {
			await callAPIAsync(logto, async (token) => {
				try {
					const res = await getAllLEAs(token);
					if (res.ok) {
						if (res.status === 204) {
							setLoadError('No LEA metrics found');
						} else {
							setLEAs(sortRecord((await res.json()) as LEADetails));
						}
					}
				} catch (e) {
					setLoadError(`Could not load metrics: ${e}`);
				}
			});
		});
	}, []);

	useEffect(() => {
		startTransition(async () => {
			await callAPIAsync(logto, async (token) => {
				try {
					const res = await getAdminUsers(token);
					if (res.ok) {
						if (res.status === 204) {
							setLoadError('No user metrics found');
						} else {
							setUsers((await res.json()) as AdminUserDetails[]);
						}
					}
				} catch (e) {
					setLoadError(`Could not load metrics: ${e}`);
				}
			});
		});
	}, []);

	useEffect(() => {
		startTransition(async () => {
			await callAPIAsync(logto, async (token) => {
				try {
					const res = await getUploadMetrics(token, {});
					if (res.ok) {
						if (res.status === 204) {
							setLoadError('No upload metrics found');
						} else {
							setFiles((await res.json()) as Record<string, string>[]);
						}
					}
				} catch (e) {
					setLoadError(`Could not load metrics: ${e}`);
				}
			});
		});
	}, []);

	useEffect(() => {
		startTransition(async () => {
			await callAPIAsync(logto, async (token) => {
				try {
					const res = await getOperationMetrics(token, {});
					if (res.ok) {
						if (res.status === 204) {
							setLoadError('No operation metrics found');
						} else {
							setOperations((await res.json()) as Record<string, string>[]);
						}
					}
				} catch (e) {
					setLoadError(`Could not load metrics: ${e}`);
				}
			});
		});
	}, []);

	const leasToDisplay = Object.fromEntries(Object.entries(leas).filter(([key]) => !excludedForces.includes(key)));

	const chartData = useMemo(() => {
		const now = new Date();
		const headers = [];
		if (filterFrom === null) {
			for (let i = 6 - 1; i >= 0; i--) {
				const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
				headers.push({
					label: d.toLocaleString('default', { month: 'short', year: 'numeric' }),
				});
			}
		} else {
			const endDate = filterTo !== null ? filterTo : dayjs();
			const numWeeks = endDate.diff(filterFrom.startOf('week').subtract(1, 'w'), 'week');
			for (let i = 0; i < numWeeks; i++) {
				const d = dayjs(filterFrom.startOf('week').add(i, 'week').add(5, 'hours'));
				headers.push({
					label: `Week commencing ${d.format('ddd Do MMM YYYY')}`,
				});
			}
		}

		return headers;
	}, [files, filterFromInput, filterToInput]);

	const handleHighlightChange = (items: AxisItemIdentifier[]) => {
		if (items && items.length > 0) {
			const index = items[0].dataIndex;

			if (typeof index === 'number') {
				setChartIndex(index);
			}
		} else {
			setChartIndex(null);
		}
	};

	const clearFilters = () => {
		setLEAFilter('');
		setFilterFrom(null);
		setFilterTo(null);
		setFilterFromInput(null);
		setFilterToInput(null);
	};

	const printDoc = useReactToPrint({
		contentRef: viewerRef,
		documentTitle: `dashboard_export`,
	});

	const dashData = processStats(files, users, leas, operations, leaFilter, filterFrom, filterTo !== null ? filterTo : dayjs());
	const headers = statsHeaders(chartIndex, chartData);
	const chartStats = processChartStats(files, users, leas, leaFilter, filterFrom, filterTo !== null ? filterTo : dayjs());

	return (
		<Stack width="100%" gap={2}>
			<Stack direction="row" gap={1}>
				<FormControl variant="outlined" fullWidth>
					<LabelledSelect
						selectLabel="Filter by LEA"
						testId="dashboard-filter-dropdown"
						value={leaFilter}
						onChange={(e) => setLEAFilter(e.target.value)}
						data-testid="lea-filter-dropdown"
						endAdornment={
							leaFilter && (
								<InputAdornment sx={{ marginRight: '10px' }} position="end">
									<Tooltip title="Clear">
										<IconButton
											onClick={() => {
												setLEAFilter('');
											}}
										>
											<ClearIcon />
										</IconButton>
									</Tooltip>
								</InputAdornment>
							)
						}
					>
						{loadingMetrics ? (
							<CircularProgress />
						) : (
							Object.keys(leasToDisplay).map((lea) => (
								<MenuItem key={lea} value={lea} data-testid={`lea-filter-${lea}`}>
									{lea}
								</MenuItem>
							))
						)}
					</LabelledSelect>
				</FormControl>
				<FormControl variant="outlined" fullWidth>
					<LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
						<DatePicker
							sx={{ width: '100%' }}
							disableFuture
							value={filterFromInput}
							onChange={(e, context) => {
								// context.validationError allows the box to be typed in without changing the data, unless the date is valid
								setFilterFromInput(e);
								if (context.validationError === null) {
									setFilterFrom(e);
								}
							}}
							minDate={dayjs('2025-09-01')}
							slotProps={{
								field: { clearable: true },
							}}
							label={'Filter from'}
						/>
					</LocalizationProvider>
				</FormControl>
				<FormControl variant="outlined" fullWidth>
					<LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
						<DatePicker
							sx={{ width: '100%' }}
							disableFuture
							value={filterToInput}
							onChange={(e, context) => {
								setFilterToInput(e);
								if (context.validationError === null) {
									setFilterTo(e);
								}
							}}
							minDate={filterFrom || dayjs('2025-09-01')}
							slotProps={{
								field: { clearable: true },
							}}
							label={'Filter to'}
						/>
					</LocalizationProvider>
				</FormControl>
				<Tooltip title="Clear filters">
					<Button variant="outlined" onClick={clearFilters}>
						<Clear />
					</Button>
				</Tooltip>
				<Tooltip title="Export dashboard">
					<Button variant="outlined" onClick={() => printDoc()} data-testid={'print-thread-button'}>
						<Download />
					</Button>
				</Tooltip>
			</Stack>
			<Stack
				gap={2}
				ref={viewerRef}
				sx={{
					'@media print': {
						'@page': {
							size: 'landscape',
							padding: '5%',
						},
					},
				}}
			>
				<Collapse in={leaFilter !== '' || filterFrom !== null || filterTo !== null}>
					<Alert severity="info" variant="outlined" data-testid="admin-dashboard-filter-alert">
						{'Showing data '}
						{leaFilter !== '' && `from ${leaFilter}`}
						{filterFrom && ` from ${filterFrom.format(`Do MMMM 'YY`)}`}
						{filterTo && ` to ${filterTo.format(`Do MMMM 'YY`)}`}
						{' only'}
					</Alert>
				</Collapse>
				{dashData.map((row, index) => (
					<Stack key={index} direction="row" width="100%" justifyContent="space-between" gap={2}>
						{row.map((stat, rowIndex) => (
							<Card sx={{ width: '100%', p: 1 }} variant="outlined" key={rowIndex}>
								<Typography variant="body1">{headers[index][rowIndex]}</Typography>
								{loadingMetrics ? (
									<Skeleton variant="rounded" height={100} />
								) : (
									<>
										<Typography
											data-testid={`${headers[index][rowIndex]}-stat`}
											sx={chartStats[index] !== undefined ? { fontSize: '32px' } : { fontSize: '48px', fontWeight: '100' }}
										>
											{chartStats[index] !== undefined && chartIndex !== null ? chartStats[index][rowIndex][chartIndex] : stat}
										</Typography>
										{chartStats[index] !== undefined && (
											<SparkLineChart
												data-testid={`${headers[index][rowIndex]}-chart`}
												onHighlightedAxisChange={handleHighlightChange}
												plotType="bar"
												data={chartStats[index][rowIndex]}
												height={100}
												showHighlight
											/>
										)}
									</>
								)}
							</Card>
						))}
					</Stack>
				))}
			</Stack>
			<SnackbarAlert
				severity="error"
				open={Boolean(loadError)}
				onClose={() => setLoadError('')}
				alertMessage={loadError}
				testId="load-error-snackbar"
			/>
		</Stack>
	);
};

export default AdminDashboard;
